import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchProductsByShowroom } from "../Redux/slice/productSlice";
import { fetchShowrooms } from "../Redux/slice/showRoomSlice";
import { Empty, Pagination, Card, Typography } from "antd";
import { 
  ShoppingCartOutlined,
  ClockCircleOutlined,
  ThunderboltOutlined,
  TagOutlined,
} from "@ant-design/icons";
import useAddToCart from "../Components/useAddToCart";

const { Title, Text } = Typography;

const CrazyPriceDrop = () => {
  const showRoomID = '67f53c84-0257-4719-a170-ff62e3d707f1';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const { productsByShowroom = {}, loading, showroom } = useSelector(
    (state) => state.products
  );

  // Cart hook
  const { addProductToCart, loading: cartLoading } = useAddToCart();
  const [addingToCart, setAddingToCart] = useState({});

  // Pagination States
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Price Filter States
  const [minPrice, setMinPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState(200000);

  // Crazy Price Drop Timer - ends July 10th at 11:59 PM
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(fetchProductsByShowroom(showRoomID));
    dispatch(fetchShowrooms());
  }, [dispatch]);

  // Countdown timer effect
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date();
      const promoEndDate = new Date('2025-07-10T23:59:59'); // July 10th, 2025 at 11:59 PM
      const difference = promoEndDate - now;

      if (difference > 0) {
        return {
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60)
        };
      } else {
        return { days: 0, hours: 0, minutes: 0, seconds: 0 };
      }
    };

    // Set initial time
    setTimeLeft(calculateTimeLeft());

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Helper functions
  const getValidImageUrl = (imagePath) => {
    if (!imagePath) return '/api/placeholder/300/300';
    return `https://smfteapi.salesmate.app/Media/Products_Images/${imagePath.split("\\").pop()}`;
  };

  const handleAddToCart = async (product) => {
    setAddingToCart(prev => ({ ...prev, [product.productID]: true }));
    
    try {
      await addProductToCart(product);
    } finally {
      setAddingToCart(prev => ({ ...prev, [product.productID]: false }));
    }
  };

  const handleProductClick = (productID, isUnavailable) => {
    // Only navigate if the product is available
    if (!isUnavailable) {
      navigate(`/product/${productID}`);
    }
  };

  // Filtering products based on price range
  const filteredProducts = (productsByShowroom[showRoomID] || [])
    .filter((product) => product.price >= minPrice && product.price <= maxPrice)
    .sort((a, b) => new Date(b.dateCreated) - new Date(a.dateCreated));

  // Pagination logic
  const indexOfLastProduct = currentPage * itemsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - itemsPerPage;
  const currentProducts = filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);

  // Format price with commas
  const formatPrice = (price) => `₵${price.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`;

  // Calculate discount percentage
  const calculateDiscount = (oldPrice, price) => {
    if (oldPrice > price) {
      return Math.round(((oldPrice - price) / oldPrice) * 100);
    }
    return 0;
  };

  // Check if promo has ended
  const promoEnded = timeLeft.days === 0 && timeLeft.hours === 0 && timeLeft.minutes === 0 && timeLeft.seconds === 0;

  return (
    <div className="min-h-screen">
      <div className="container mx-auto p-4 md:p-2 relative z-10">
        
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 left-10 w-24 h-24 bg-gradient-to-r from-orange-400 to-yellow-400 rounded-full opacity-20 animate-pulse"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-r from-green-400 to-emerald-400 rounded-full opacity-20 animate-bounce"></div>
          <div className="absolute bottom-20 left-1/4 w-20 h-20 bg-gradient-to-r from-orange-400 to-green-400 rounded-full opacity-20 animate-pulse"></div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-1 mt-2">
          {/* Crazy Price Drop Header */}
          <div className="flex items-center gap-3">
            
          </div>

          {/* Countdown Timer */}
          
        </div>

        {/* Promo Banner */}
        <div className="bg-gradient-to-r from-orange-500 via-yellow-400 to-yellow-500 text-white rounded-3xl p-1 md:p-4 mb-2 shadow-2xl">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="text-center md:text-left">
              <h2 className="text-2xl md:text-3xl font-black ">🔥 CRAZY PRICE DROP IS LIVE! 🔥</h2>
              <p className="text-lg md:text-xl font-semibold text-yellow-200">
                 • Save Big! 
              </p>
            </div>
            <div className="mt-4 md:mt-0">
             <div className="flex flex-wrap items-center justify-start md:justify-center gap-2 bg-gradient-to-r from-green-700 via-green-500 to-green-500 text-white px-6 py-3 rounded-full shadow-lg text-sm md:text-base ">
     
            {promoEnded ? (
              <Text className="text-white font-bold">Promo Ended</Text>
            ) : (
              <div className="flex flex-wrap items-center gap-1 font-bold">
                <span className="text-yellow-200">Ends in:</span>
                <span className="bg-white/25 px-3 py-1 rounded-lg text-sm md:text-base font-black">{timeLeft.days}d</span>
                <span className="bg-white/25 px-3 py-1 rounded-lg text-sm md:text-base font-black">{String(timeLeft.hours).padStart(2, '0')}h</span>
                <span className="bg-white/25 px-3 py-1 rounded-lg text-sm md:text-base font-black">{String(timeLeft.minutes).padStart(2, '0')}m</span>
                <span className="bg-white/25 px-3 py-1 rounded-lg text-sm md:text-base font-black">{String(timeLeft.seconds).padStart(2, '0')}s</span>
              </div>
            )}
          </div>
            </div>
          </div>
        </div>

        {/* Products Section */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {Array.from({ length: itemsPerPage }).map((_, index) => (
              <Card key={index} className="animate-pulse shadow rounded-2xl border-0">
                <div className="h-40 md:h-48 bg-gray-300 rounded-t-2xl" />
                <div className="p-3 space-y-2">
                  <div className="h-4 bg-gray-300 rounded w-3/4" />
                  <div className="h-3 bg-gray-200 rounded w-1/2" />
                </div>
              </Card>
            ))}
          </div>
        ) : currentProducts.length > 0 ? (
          <>
            {/* Products Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
              {currentProducts.map((product) => {
                const discount = calculateDiscount(product.oldPrice, product.price);
                const soldOut = product.stock === 0;
                // Check if brandName is "All brands" - treat as out of stock
                const isAllBrands = product.brandName === "All brands";
                const isUnavailable = soldOut || isAllBrands;
                const isAddingToCart = addingToCart[product.productID];
                
                return (
                  <div
                    key={product.productID}
                    className={`group bg-white rounded-2xl shadow-lg overflow-hidden relative transition-all duration-300 border-2 ${
                      isUnavailable 
                        ? 'opacity-60 cursor-not-allowed border-gray-200' 
                        : 'hover:shadow-2xl transform hover:-translate-y-2 cursor-pointer border-transparent '
                    }`}
                    onClick={() => handleProductClick(product.productID, isUnavailable)}
                  >
                    {/* Badges */}
                    {isAllBrands ? (
                      <span className="absolute top-2 left-2 bg-gray-800 text-white text-xs px-2 py-1 rounded-full z-10 font-bold">
                        OUT OF STOCK
                      </span>
                    ) : soldOut ? (
                      <span className="absolute top-2 left-2 bg-gray-800 text-white text-xs px-2 py-1 rounded-full z-10 font-bold">
                        SOLD OUT
                      </span>
                    ) : discount > 0 ? (
                      <span className="absolute top-2 left-2 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-black px-3 py-1 rounded-full z-10 animate-pulse">
                        -{discount}% OFF
                      </span>
                    ) : (
                      <span className="absolute top-2 left-2 bg-gradient-to-r from-orange-500 to-yellow-500 text-white text-xs font-black px-3 py-1 rounded-full z-10">
                        CRAZY DEAL
                      </span>
                    )}

                    {/* Price Drop Badge */}
                    {!isUnavailable && (
                      <span className="absolute top-2 right-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white text-xs font-black px-2 py-1 rounded-full z-10">
                        PRICE DROP
                      </span>
                    )}

                    {/* Image */}
                    <div className="relative overflow-hidden">
                      <div className={`h-40 md:h-48 w-full flex items-center justify-center  ${
                        isUnavailable ? 'grayscale' : ''
                      }`}>
                        <img
                          src={getValidImageUrl(product.productImage)}
                          alt={product.productName}
                          className={`h-full w-full object-contain transition-transform duration-500 ${
                            !isUnavailable ? 'group-hover:scale-110' : ''
                          }`}
                        />
                      </div>

                      {/* Gradient Overlay - Only for available products */}
                      {!isUnavailable && (
                        <div className="absolute inset-0 bg-gradient-to-t from-orange-900/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                      )}
                    </div>

                    {/* Product Info */}
                    <div className="p-3 text-center">
                      <h3 className={`text-xs md:text-sm font-semibold line-clamp-2 mb-2 transition-colors ${
                        isUnavailable 
                          ? 'text-gray-500' 
                          : 'text-gray-800 group-hover:text-orange-600'
                      }`}>
                        {product.productName}
                      </h3>
                      
                      {/* Price Section */}
                      <div className="space-y-1">
                        <div className="flex justify-center items-center gap-2">
                          <span className={`font-black text-md md:text-lg  ${
                            isUnavailable ? 'text-gray-500' : 'text-orange-600'
                          }`}>
                            {formatPrice(product.price)}
                          </span>
                          {product.oldPrice > 0 && (
                            <span className="text-sm text-gray-400 line-through font-medium">
                              {formatPrice(product.oldPrice)}
                            </span>
                          )}
                        </div>
                       
                      </div>

                      {/* Action Buttons - Only show for available products */}
                      {!isUnavailable && (
                        <div className="space-y-2 mt-3 transform translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-300">
                          {/* Add to Cart Button */}
                          <button
                            className="w-full bg-gradient-to-r from-orange-500 via-orange-600 to-yellow-500 text-white px-2 py-1 rounded-xl font-bold hover:from-orange-600 hover:via-orange-700 hover:to-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2 shadow-lg"
                            disabled={isAddingToCart}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddToCart(product);
                            }}
                          >
                            <ShoppingCartOutlined />
                            {isAddingToCart ? "Adding..." : "Add to Cart"}
                          </button>
                        </div>
                      )}

                      {/* Unavailable message for out of stock items */}
                      {isUnavailable && (
                        <div className="mt-3">
                          <div className="bg-gray-100 text-gray-500 px-4 py-2 rounded-xl font-bold text-xs md:text-sm">
                            {isAllBrands ? "Out of Stock" : "Sold Out"}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            {filteredProducts.length > itemsPerPage && (
              <div className="flex justify-center mt-12">
                <div className="bg-white rounded-3xl shadow-2xl p-6 border-2 border-orange-100">
                  <Pagination
                    current={currentPage}
                    total={filteredProducts.length}
                    pageSize={itemsPerPage}
                    onChange={(page) => setCurrentPage(page)}
                    showSizeChanger={false}
                    size="large"
                    showQuickJumper
                    showTotal={(total, range) => 
                      `${range[0]}-${range[1]} of ${total} crazy deals`
                    }
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex flex-col items-center justify-center mt-16 bg-white rounded-3xl shadow-2xl p-12 border-2 border-orange-100">
            <Empty
              description={
                <div className="text-center">
                  <Title level={3} className="text-gray-600 mb-4">No Crazy Price Drop Deals Found</Title>
                  <Text className="text-gray-500 text-lg">
                    Adjust your filters or check back soon for more amazing price drops!
                  </Text>
                </div>
              }
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              imageStyle={{ height: 200, marginBottom: 24 }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CrazyPriceDrop;